import { Loader2, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/base/button";
import { useStateMachine } from "@/core/arch/state_machine";
import {
  isUnenriched,
  PoyntSaleBackfillBloc,
  shownResult,
  type BackfillCounterKey,
  type BackfillOutcome,
  type BackfillOutcomeRow,
  type PoyntSaleBackfillForm,
  type PoyntSaleBackfillResult,
  type ShownResult,
} from "@/application/tools/poynt_sale_backfill_bloc";
import { poyntSaleBackfillRepository } from "@/application/tools/poynt_sale_backfill_repository";
import { formatCurrency } from "@/application/money/types";
import { ToolSheetShell } from "./ToolSheetShell";
import type { ToolSheetProps } from "../tools";

const COUNTERS: { key: BackfillCounterKey; label: string }[] = [
  { key: "poyntSales", label: "Poynt sales" },
  { key: "eligible", label: "Eligible" },
  { key: "written", label: "Written" },
  { key: "alreadyInCpos", label: "Already in CPOS" },
  { key: "enriched", label: "Enriched" },
  { key: "unenriched", label: "Unenriched" },
  { key: "possibleCashRemainder", label: "Possible cash remainder" },
  { key: "otherApp", label: "Other app" },
  { key: "declined", label: "Declined" },
  { key: "tooRecent", label: "Too recent" },
  { key: "notPreviewed", label: "Not previewed" },
  { key: "unparseable", label: "Unparseable" },
  { key: "errors", label: "Errors" },
];

const OUTCOME_LABELS: Record<BackfillOutcome, string> = {
  planned: "Planned",
  written: "Written",
  alreadyInCpos: "Already in CPOS",
  unenriched: "Unenriched",
  skipped: "Skipped",
  error: "Error",
};

function outcomeClass(outcome: BackfillOutcome): string {
  if (outcome === "planned" || outcome === "written") {
    return "text-emerald-700 bg-emerald-50";
  }
  if (outcome === "error") return "text-red-700 bg-red-50";
  if (outcome === "unenriched") return "text-amber-700 bg-amber-50";
  return "text-subtle-foreground bg-muted";
}

/** Sales are grouped by Toronto day, so times are shown in Toronto too. */
function formatTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso || "—";
  return date.toLocaleString("en-CA", {
    timeZone: "America/Toronto",
    dateStyle: "short",
    timeStyle: "short",
  });
}

function feeTaxAndStaff(row: BackfillOutcomeRow): string {
  const e = row.enrichment;
  if (!e) return "—";
  const parts = [
    e.processingFee != null
      ? `fee ${formatCurrency(e.processingFee, "cad")}`
      : null,
    e.taxAmount != null ? `tax ${formatCurrency(e.taxAmount, "cad")}` : null,
    e.staffId ? `staff ${e.staffId}` : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function OutcomeTable({ result }: { result: PoyntSaleBackfillResult }) {
  if (result.outcomes.length === 0) return null;
  return (
    <>
      <div className="border-border mt-4 max-h-96 overflow-auto rounded-md border">
        <table className="w-full text-left text-xs">
          <thead className="bg-muted text-muted-foreground sticky top-0">
            <tr>
              <th className="px-3 py-2 font-medium">Poynt ID</th>
              <th className="px-3 py-2 font-medium">Time</th>
              <th className="px-3 py-2 text-right font-medium">Amount</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Outcome</th>
              <th className="px-3 py-2 font-medium">Reason</th>
              <th className="px-3 py-2 font-medium">Fee / tax / staff</th>
            </tr>
          </thead>
          <tbody className="divide-border divide-y">
            {result.outcomes.map((row) => (
              <tr key={row.poyntTransactionId} className="align-top">
                <td className="text-foreground px-3 py-2 font-mono break-all">
                  {row.poyntTransactionId}
                </td>
                <td className="text-muted-foreground px-3 py-2 whitespace-nowrap">
                  {formatTime(row.createdAt)}
                </td>
                <td className="text-foreground px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {formatCurrency(row.amount, "cad")}
                </td>
                <td className="text-muted-foreground px-3 py-2">
                  {row.status}
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`inline-block rounded px-1.5 py-0.5 whitespace-nowrap ${outcomeClass(row.outcome)}`}
                  >
                    {OUTCOME_LABELS[row.outcome]}
                  </span>
                  {isUnenriched(row) && (
                    <span className="ml-1 inline-block rounded bg-amber-50 px-1.5 py-0.5 whitespace-nowrap text-amber-700">
                      Unenriched
                    </span>
                  )}
                </td>
                <td className="text-muted-foreground px-3 py-2">
                  {row.reason ?? "—"}
                </td>
                <td className="text-muted-foreground px-3 py-2">
                  {feeTaxAndStaff(row)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {result.truncated && (
        <p className="text-subtle-foreground mt-2 text-xs">
          Showing the first {result.outcomes.length} sales. The counts above
          cover all of them.
        </p>
      )}
    </>
  );
}

function ResultView({ result, input, stoppedAt }: ShownResult) {
  if (result.status === "unmapped") {
    return (
      <div className="mt-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <p className="text-sm text-amber-800">
          This merchant has no linked Poynt business, so there is nothing to
          read or write.
        </p>
      </div>
    );
  }

  return (
    <>
      <p className="text-muted-foreground mt-4 text-xs">
        Merchant{" "}
        <span className="text-foreground font-mono">{input.businessId}</span>
        {" · "}
        <span className="text-foreground">
          {input.from} – {input.to}
        </span>
      </p>
      <p className="text-muted-foreground mt-1 text-xs">
        Poynt business{result.poyntBusinessIds.length === 1 ? "" : "es"}:{" "}
        <span className="text-foreground font-mono">
          {result.poyntBusinessIds.join(", ") || "—"}
        </span>
      </p>

      {stoppedAt && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs text-amber-800">
            Stopped at {stoppedAt}: run {stoppedAt}–{input.to} next.
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {COUNTERS.map(({ key, label }) => (
          <div
            key={key}
            className="border-border bg-muted rounded-md border px-3 py-2"
          >
            <p className="text-muted-foreground text-xs">{label}</p>
            <p className="text-foreground mt-0.5 text-lg font-semibold tabular-nums">
              {/* eslint-disable-next-line security/detect-object-injection -- key comes from the fixed COUNTERS list */}
              {result.counters[key]}
            </p>
          </div>
        ))}
      </div>

      <OutcomeTable result={result} />
    </>
  );
}

const FIELDS: {
  name: keyof PoyntSaleBackfillForm;
  label: string;
  type: "text" | "date";
  hint?: string;
}[] = [
  { name: "businessId", label: "Merchant business ID", type: "text" },
  { name: "from", label: "From", type: "date" },
  { name: "to", label: "To", type: "date" },
  {
    name: "poyntBusinessId",
    label: "Poynt business ID (optional)",
    type: "text",
    hint: "Only needed when the merchant has more than one.",
  },
];

/**
 * Finds Poynt sales missing from CPOS for one merchant and a window of
 * Toronto days. Preview first: the write run only creates what the preview
 * listed.
 */
export function PoyntSaleBackfillSheet({ onClose }: ToolSheetProps) {
  const [state, bloc] = useStateMachine(
    () => new PoyntSaleBackfillBloc(poyntSaleBackfillRepository),
  );
  const { phase, form } = state;

  const editable = phase.kind === "composing" || phase.kind === "error";
  const canPreview =
    form.businessId.trim() !== "" && form.from !== "" && form.to !== "";
  const shown = shownResult(phase);
  const showForm =
    editable ||
    phase.kind === "previewing" ||
    (phase.kind === "starting" && !phase.preview);

  return (
    <ToolSheetShell
      title="Poynt Sale Backfill"
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          {phase.kind === "composing" && (
            <Button
              className="flex-1"
              disabled={!canPreview}
              onClick={() => bloc.preview()}
            >
              Preview
            </Button>
          )}
          {(phase.kind === "starting" || phase.kind === "previewing") && (
            <Button disabled className="flex-1">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              {phase.kind === "starting" ? "Starting…" : "Previewing…"}
            </Button>
          )}
          {phase.kind === "previewed" && (
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => bloc.reset()}
              >
                Back
              </Button>
              {phase.result.status === "ok" && (
                <Button
                  className="flex-1"
                  disabled={phase.result.plannedIds.length === 0}
                  onClick={() => bloc.write()}
                >
                  Write {phase.result.plannedIds.length}{" "}
                  {phase.result.plannedIds.length === 1 ? "sale" : "sales"}
                </Button>
              )}
            </>
          )}
          {phase.kind === "writing" && (
            <Button disabled className="flex-1">
              <Loader2 className="mr-2 h-3 w-3 animate-spin" />
              Writing…
            </Button>
          )}
          {(phase.kind === "written" || phase.kind === "error") && (
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => bloc.reset()}
            >
              {phase.kind === "written" ? "Start over" : "Back"}
            </Button>
          )}
        </div>
      }
    >
      <p className="text-muted-foreground text-sm">
        Finds sales that Poynt has but CPOS does not, for one merchant over a
        window of up to 62 days (Toronto days, both ends included). Preview
        reads Poynt and writes nothing. Writing creates only the sales the
        preview listed.
      </p>

      {showForm && (
        <div className="mt-4 space-y-3">
          {FIELDS.map((field) => (
            <div key={field.name}>
              <label
                htmlFor={`psb-${field.name}`}
                className="text-muted-foreground text-xs font-medium"
              >
                {field.label}
              </label>
              <input
                id={`psb-${field.name}`}
                type={field.type}
                value={form[field.name]}
                onChange={(e) => bloc.setField(field.name, e.target.value)}
                disabled={!editable}
                spellCheck={false}
                className="border-border text-foreground disabled:bg-muted mt-1 w-full rounded-md border p-2 font-mono text-xs focus:border-blue-400 focus:outline-none"
              />
              {field.hint && (
                <p className="text-subtle-foreground mt-1 text-xs">
                  {field.hint}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {phase.kind === "error" && (
        <div className="mt-6 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="text-sm break-words text-red-700">{phase.message}</p>
        </div>
      )}

      {phase.kind === "writing" && (
        <p className="text-muted-foreground mt-4 text-sm">
          Writing the previewed sales. Keep this open until it finishes.
        </p>
      )}

      {shown && (
        <>
          {phase.kind === "written" && (
            <p className="text-foreground mt-4 text-sm font-medium">
              Write finished: {phase.result.writtenIds.length}{" "}
              {phase.result.writtenIds.length === 1 ? "row" : "rows"} created.
            </p>
          )}
          <ResultView {...shown} />
        </>
      )}
    </ToolSheetShell>
  );
}
